import { useState, useEffect } from 'react';
import { AlertCircle, TrendingUp, Users, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const cur = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

export default function FollowupTab({ token }) {
    const [stats, setStats] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        const load = async () => {
            try {
                const [sr, ar] = await Promise.all([
                    fetch(`${API}/api/followup/stats`, { headers }),
                    fetch(`${API}/api/followup/abandoned`, { headers }),
                ]);
                setStats(await sr.json());
                setSessions((await ar.json()).sessions || []);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) return <div className="text-gray-500">Laden...</div>;

    return (
        <div className="space-y-6" data-testid="followup-tab">
            <div>
                <h2 className="text-xl font-bold font-['Outfit']">Verlaten Calculator-sessies</h2>
                <p className="text-sm text-gray-500">Bezoekers die de calculator gebruikten maar geen formulier invulden</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Totaal sessies" value={stats.total_sessions} icon={Users} color="blue" />
                <StatCard label="Ingevuld" value={stats.form_submitted} icon={CheckCircle} color="emerald" />
                <StatCard label="Verlaten" value={stats.abandoned} icon={AlertCircle} color="amber" />
                <StatCard label="Conversie" value={`${stats.conversion_rate}%`} icon={TrendingUp} color="emerald" />
            </div>

            {sessions.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500">
                    Nog geen verlaten sessies. Sessies verschijnen hier zodra bezoekers de calculator gebruiken maar geen offerte aanvragen.
                </div>
            ) : (
                <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="text-xs text-gray-600 uppercase border-b border-gray-800">
                            <tr>
                                <th className="px-4 py-3 text-left">Sessie ID</th>
                                <th className="px-4 py-3 text-center">Aantal douches</th>
                                <th className="px-4 py-3 text-right">Berekende winst/mnd</th>
                                <th className="px-4 py-3 text-center">Interacties</th>
                                <th className="px-4 py-3 text-left">Laatst actief</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.map((s, i) => {
                                const d = s.calculator_data || {};
                                return (
                                    <tr key={i} className="border-b border-gray-800/50" data-testid={`session-row-${s.session_id}`}>
                                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">{s.session_id.slice(0, 12)}...</td>
                                        <td className="px-4 py-3 text-center text-gray-300">{d.aantalDouches || '-'}</td>
                                        <td className="px-4 py-3 text-right text-emerald-400 font-medium">
                                            {d.nettoWinstMaand ? cur(d.nettoWinstMaand) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-center text-gray-300">{s.interactions}</td>
                                        <td className="px-4 py-3 text-gray-500 text-xs">
                                            {s.updated_at ? new Date(s.updated_at).toLocaleString('nl-NL') : '-'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, icon: Icon, color }) {
    const styles = {
        blue: 'border-blue-500/20 bg-blue-500/5 text-blue-400',
        emerald: 'border-emerald-500/20 bg-emerald-500/5 text-emerald-400',
        amber: 'border-amber-500/20 bg-amber-500/5 text-amber-400',
    };
    return (
        <div className={`rounded-xl border p-5 ${styles[color]}`}>
            <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider font-semibold opacity-80">{label}</span>
            </div>
            <div className="text-2xl font-bold text-white font-['Outfit'] tabular-nums">{value}</div>
        </div>
    );
}
